/**
 * Office / Teams Service - API client pour Office Add-in + Teams integration
 */
import { config } from '../config';
import FamlaServices from './FamlaServices';

const API_BASE = config.api.baseUrl;

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: string;
}

export interface ContextPayload {
    hostApp: string;
    hostPlatform?: string;
    contextText: string;
    contextType: 'email-thread' | 'document' | 'excel-matrix' | 'unknown';
    subject?: string;
    documentTitle?: string;
    cellRange?: string;
    emailBody?: string;
    recipients?: string[];
    outlookItemId?: string;
    conversationId?: string;
    desktopReturnUrl?: string;
}

export interface HandoffPayload {
    context: ContextPayload;
    threadMessages: ChatMessage[];
    userAzureId: string;
    userEmail: string;
    userName: string;
}

export interface HandoffResponse {
    sessionToken: string;
}

export interface HandoffRetrieveData {
    context: ContextPayload;
    threadMessages: ChatMessage[];
    userEmail: string;
    userName: string;
    companyId?: string;
}

export interface AskProcessPayload {
    hostApp: string;
    contextText: string;
    contextType: string;
    question: string;
    threadHistory: ChatMessage[];
    userAzureId?: string;
    userEmail?: string;
}

export interface AskProcessResponse {
    answer: string;
    isGeneralKnowledge?: boolean;
    source?: {
        processMapName?: string;
        stepNumber?: number;
        documentName?: string;
        deepLink?: string;
    };
}

export interface FlagPainPointPayload {
    hostApp: string;
    contextText: string;
    contextType: string;
    subject?: string;
    userMessage: string;
    userAzureId: string;
    userEmail: string;
    userName: string;
}

export interface FlagPainPointResponse {
    success: boolean;
    flagId: string;
    notifiedAdmins: number;
}

export interface StakeholderInfo {
    email: string;
    displayName?: string;
    azureId?: string;
}

export interface OfficeSettingsData {
    company_id: string;
    teams_webhook_url?: string | null;
    flag_routing_email?: string | null;
    bot_connected?: boolean;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface ContributorInvitePayload {
    processId?: string;
    processName?: string;
    stepId?: string;
    stepLabel?: string;
    question?: string;
    contributors: Array<{
        email: string;
        displayName?: string;
        azureId?: string;
        question?: string;
    }>;
}

export interface TeamsMapSummary {
    id: string;
    title: string;
    description?: string;
    ownerName?: string;
    updatedAt?: string;
    stepCount: number;
    publish: boolean;
    viewUrl: string;
    embedUrl?: string;
}

class OfficeService {
    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const url = `${API_BASE}${endpoint}`;

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        const response = await fetch(url, {
            ...options,
            headers,
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.description || `Request failed: ${response.status}`);
        }

        const data = await response.json();
        return data.data ?? data;
    }

    private async authRequest<T>(
        endpoint: string,
        accessToken: string,
        options: RequestInit = {}
    ): Promise<T> {
        const response = await FamlaServices(
            'api_host',
            endpoint,
            (options.method as any) || 'GET',
            options.body ? JSON.parse(options.body as string) : '',
            accessToken
        );

        if (response.status < 200 || response.status >= 300) {
            const description = response.body?.description || `Request failed: ${response.status}`;
            throw new Error(description);
        }

        return response.body?.data ?? response.body;
    }

    async createHandoff(payload: HandoffPayload): Promise<HandoffResponse> {
        return this.request<HandoffResponse>('/session/handoff', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async retrieveHandoff(sessionToken: string, accessToken: string): Promise<HandoffRetrieveData> {
        const response = await FamlaServices(
            'api_host',
            `/session/handoff/${encodeURIComponent(sessionToken)}`,
            'GET',
            '',
            accessToken
        );

        if (response.status !== 200) {
            const description = response.body?.description || `Request failed: ${response.status}`;
            throw new Error(description);
        }

        return response.body?.data ?? response.body;
    }

    async askAboutProcess(payload: AskProcessPayload): Promise<AskProcessResponse> {
        return this.request<AskProcessResponse>('/office/ask', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async flagPainPoint(payload: FlagPainPointPayload): Promise<FlagPainPointResponse> {
        return this.request<FlagPainPointResponse>('/andon/flag', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async discoverStakeholders(outlookItemId: string, accessToken: string): Promise<StakeholderInfo[]> {
        const data = await this.request<{ stakeholders: StakeholderInfo[] }>('/office/stakeholders', {
            method: 'POST',
            body: JSON.stringify({ outlookItemId, accessToken }),
        });
        return data.stakeholders || [];
    }

    async getOfficeSettings(accessToken: string): Promise<OfficeSettingsData> {
        return this.authRequest<OfficeSettingsData>('/office/settings', accessToken);
    }

    async updateOfficeSettings(
        accessToken: string,
        payload: { teams_webhook_url?: string | null; flag_routing_email?: string | null }
    ): Promise<OfficeSettingsData> {
        return this.authRequest<OfficeSettingsData>('/office/settings', accessToken, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }

    async inviteContributors(accessToken: string, payload: ContributorInvitePayload) {
        return this.authRequest('/teams/discovery/invite', accessToken, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    /** Invite from Office sidebar without Famla JWT (inviterEmail resolves company). */
    async inviteContributorsPublic(payload: ContributorInvitePayload & {
        inviterEmail?: string;
        inviterAzureId?: string;
    }) {
        return this.request('/teams/discovery/invite', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async listTeamsMaps(accessToken: string): Promise<TeamsMapSummary[]> {
        const data = await this.authRequest<{ maps: TeamsMapSummary[] }>('/teams/maps', accessToken);
        return data.maps || [];
    }

    async getSharedMap(shareToken: string) {
        return this.request(`/teams/share/${encodeURIComponent(shareToken)}`);
    }

    async getBotStatus(accessToken: string) {
        return this.authRequest<{ connected: boolean; azureId?: string; installHint?: string }>(
            '/teams/bot-status',
            accessToken
        );
    }
}

export const officeService = new OfficeService();
export default officeService;
